import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  NodeResizeControl,
  ResizeControlVariant,
  applyNodeChanges,
  useNodesState,
  useReactFlow,
  type Node,
  type NodeChange,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { X, Rows3, Layers, Grid3x3, GitBranch, Share2, Link2, StickyNote, Workflow, MoreVertical, Copy, type LucideIcon } from 'lucide-react';

import { VisualizationArrayGraphCanvas } from './VisualizationArrayGraphCanvas';
import { VisualizationGridCanvas } from './VisualizationGridCanvas';
import { VisualizationTreeGraphCanvas } from './VisualizationTreeGraphCanvas';
import { VisualizationNoteCanvas } from './VisualizationNoteCanvas';
import { VisualizationDiagramCanvas } from './VisualizationDiagramCanvas';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type {
  View,
  ViewKind,
  ArrayView,
  StackView,
  GridView,
  TreeView,
  GraphView,
  NoteView,
  DiagramView,
} from '@/features/Visualizer/types';

interface Props {
  views: View[];
  onChangeView: (index: number, next: View) => void;
  onRemoveView: (index: number) => void;
  onDuplicateView: (index: number) => void;
  pendingNewViewPosition: { index: number; position: { x: number; y: number } } | null;
  onConsumedPendingPosition: () => void;
  onDropViewKind: (kind: ViewKind, flowPosition: { x: number; y: number }) => void;
}

interface BlockData {
  view: View;
  index: number;
  onChangeView: (index: number, next: View) => void;
  onRemoveView: (index: number) => void;
  onDuplicateView: (index: number) => void;
}

export const KIND_LABEL: Record<View['kind'], string> = {
  array: 'Array',
  stack: 'Stack',
  grid: 'Grid',
  tree: 'Tree',
  graph: 'Graph',
  linkedlist: 'Linked list',
  note: 'Note',
  diagram: 'Diagram',
};

export const KIND_STYLE: Record<View['kind'], { icon: LucideIcon; className: string }> = {
  array: { icon: Rows3, className: 'bg-[#eff6ff] border-[#bfdbfe] text-[#1d4ed8]' },
  stack: { icon: Layers, className: 'bg-[#f5f3ff] border-[#ddd6fe] text-[#6d28d9]' },
  grid: { icon: Grid3x3, className: 'bg-[#ecfdf5] border-[#a7f3d0] text-[#047857]' },
  tree: { icon: GitBranch, className: 'bg-[#f0fdf4] border-[#bbf7d0] text-[#15803d]' },
  graph: { icon: Share2, className: 'bg-[#eef2ff] border-[#c7d2fe] text-[#4338ca]' },
  linkedlist: { icon: Link2, className: 'bg-[#f8fafc] border-[#e2e8f0] text-[#475569]' },
  note: { icon: StickyNote, className: 'bg-[#fffbeb] border-[#fde68a] text-[#b45309]' },
  diagram: { icon: Workflow, className: 'bg-[#fff1f2] border-[#fecdd3] text-[#be123c]' },
};

/**
 * One draggable card per view on the shared stage. Dragging the card moves
 * it around the canvas (React Flow's own node-drag, proven reliable); the
 * `nodrag nopan` wrapper below stops that same drag from firing when the
 * pointer lands on the block's own controls (cells, inputs, buttons, or —
 * for tree/graph — a second, nested React Flow canvas), so editing a value
 * never accidentally repositions the whole block.
 */
function ViewBlockNode({ data, selected, width, height }: NodeProps) {
  const { view, index, onChangeView, onRemoveView, onDuplicateView } = data as unknown as BlockData;
  const style = KIND_STYLE[view.kind];
  const Icon = style.icon;

  /**
   * Resizing scales the block's whole content like an image, not just its
   * frame — a trainer asked for the cells/nodes themselves to grow/shrink,
   * not just more blank canvas around them. `contentRef` measures the
   * content's own natural, unscaled layout size (its `offsetWidth`/
   * `offsetHeight`, which a CSS `transform` never affects — only `scale(...)`
   * paints it differently, the box model underneath is untouched) via
   * `ResizeObserver`, so it re-measures whenever the content's real size
   * changes too (e.g. adding an array cell). `scaleX`/`scaleY` is then just
   * "how much bigger is the resized box than that natural size" — 1 (no-op)
   * until the trainer actually drags a handle, since `width`/`height` (from
   * `NodeResizeControl`, via `NodeProps`) are undefined before any resize.
   */
  const contentRef = useRef<HTMLDivElement>(null);
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      setNaturalSize({ width: el.offsetWidth, height: el.offsetHeight });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const scaleX = width && naturalSize?.width ? width / naturalSize.width : 1;
  const scaleY = height && naturalSize?.height ? height / naturalSize.height : 1;

  /**
   * While actively dragging a resize handle, the cursor sweeps across the
   * content below on its way to/from the edge — for Array specifically,
   * that content has its own `group-hover` reveal (the insert-gap "+"
   * buttons), which would otherwise flash on for every cell purely because
   * the mouse passed over them mid-resize, not because the trainer wants to
   * insert anything. Threaded down as `suppressHoverAffordances`.
   */
  const [isResizing, setIsResizing] = useState(false);
  const resizeHandlers = {
    onResizeStart: () => setIsResizing(true),
    onResizeEnd: () => setIsResizing(false),
  };

  return (
    <div className="relative h-full w-full rounded-lg border border-[#d1d5db] bg-white shadow-sm">
      {/* Resize handles only while the block is selected (React Flow's own
          click-to-select, not the custom `selectedViewIndex` plumbing removed
          along with the sidebar pilot) — otherwise every block on a busy
          stage would show eight little squares at once. Only right/bottom/
          bottom-right — no top-side or left-side controls — so nothing sits
          near the header's kebab menu in the top-right corner; the header
          stays anchored where it's dragged, only the content below grows.
          Dragging a handle sets `node.width`/`node.height` directly via
          React Flow's store; `onNodesChange` (already wired below) picks up
          that resulting `dimensions` change like any other. Resized size is
          exactly as session-local as position (never written into the
          persisted `View` — see `positionsRef`'s doc comment above).
          Deliberately siblings of the `overflow-hidden` wrapper below, not
          children of it: a handle/line is positioned STRADDLING the node's
          border (half in, half out), so nesting them inside the
          content-clipping wrapper clipped them to invisible — this outer
          div has no `overflow` set, only the wrapper around the scaled
          content does. */}
      {selected && (
        <>
          {/* `Line` variant, not the default `Handle` dot — a line control's
              drag target spans the FULL edge, not a single 5x5px point along
              it (using `Handle` here, as first tried, made the right/bottom
              edges nearly impossible to actually grab). The library's own
              default line hit-box is a literal 1px-wide strip with no
              padding — still awkward for a real mouse — so it's explicitly
              widened here; it's already fully transparent, so a wider
              invisible hit box changes nothing visible. */}
          <NodeResizeControl
            variant={ResizeControlVariant.Line}
            position="right"
            minWidth={280}
            minHeight={100}
            className="nodrag"
            style={{ width: 10, marginLeft: -5 }}
            {...resizeHandlers}
          />
          <NodeResizeControl
            variant={ResizeControlVariant.Line}
            position="bottom"
            minWidth={280}
            minHeight={100}
            className="nodrag"
            style={{ height: 10, marginTop: -5 }}
            {...resizeHandlers}
          />
          {/* Default corner handle is 5x5px — also enlarged for a real
              mouse; unlike the transparent line strips above, this one is
              actually visible, so making it bigger is a visible (desirable)
              change, not just a bigger invisible hit box. */}
          <NodeResizeControl
            position="bottom-right"
            minWidth={280}
            minHeight={100}
            className="nodrag"
            style={{ width: 10, height: 10 }}
            {...resizeHandlers}
          />
        </>
      )}
      <div className="h-full w-full overflow-hidden rounded-lg">
      <div
        ref={contentRef}
        style={{ transform: `scale(${scaleX}, ${scaleY})`, transformOrigin: 'top left', width: 'max-content' }}
        className="flex flex-col"
      >
        <div
          className={`flex shrink-0 cursor-grab items-center justify-between gap-3 rounded-t-lg border-b px-2 py-1 active:cursor-grabbing ${style.className}`}
        >
        <span className="flex items-center gap-1.5 text-[11px] font-semibold">
          <Icon className="h-3.5 w-3.5" />
          {KIND_LABEL[view.kind]}
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`${KIND_LABEL[view.kind]} block actions`}
              className="nodrag rounded p-0.5 hover:bg-black/10"
            >
              <MoreVertical className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {/*
              `stopPropagation()` here is new and required by this task:
              `DropdownMenuContent` renders through a React Portal, but React's
              synthetic events still bubble through the *component* tree (not
              the DOM tree) across portals — so without this, clicking
              "Duplicate"/"Remove" would ALSO fire the new `onNodeClick` handler
              added below (same click, same synthetic-event bubble phase),
              re-selecting this node with its pre-mutation index right after
              `onDuplicateView`/`onRemoveView`'s own selection bookkeeping
              (Task 2) runs — silently stomping it. The kebab TRIGGER button
              does not need this: reselecting the same node when merely opening
              the menu is harmless and intentional (see the design doc).
            */}
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onDuplicateView(index);
              }}
            >
              <Copy className="h-3.5 w-3.5" /> Duplicate
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onRemoveView(index);
              }}
            >
              {/* `text-destructive` must live on this inner span, not the item itself: the shared
                  `DropdownMenuItem` hardcodes `data-[highlighted]:text-foreground`, whose
                  attribute-selector variant outranks a plain `text-destructive` utility on the
                  item and would flip Remove back to the default color on hover/keyboard focus.
                  A directly-set color on this child wins over that inherited override. */}
              <span className="flex items-center gap-1.5 text-destructive">
                <X className="h-3.5 w-3.5" /> Remove
              </span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="nodrag nopan cursor-default p-2">
        {view.kind === 'array' ? (
          <VisualizationArrayGraphCanvas
            kind="array"
            value={view as ArrayView}
            onChange={(next) => onChangeView(index, next)}
            suppressHoverAffordances={isResizing}
          />
        ) : view.kind === 'stack' ? (
          <VisualizationArrayGraphCanvas
            kind="stack"
            value={view as StackView}
            onChange={(next) => onChangeView(index, next)}
            suppressHoverAffordances={isResizing}
          />
        ) : view.kind === 'grid' ? (
          <VisualizationGridCanvas value={view as GridView} onChange={(next) => onChangeView(index, next)} />
        ) : view.kind === 'tree' || view.kind === 'graph' ? (
          <VisualizationTreeGraphCanvas
            kind={view.kind}
            value={view as TreeView | GraphView}
            onChange={(next) => onChangeView(index, next)}
          />
        ) : view.kind === 'note' ? (
          <VisualizationNoteCanvas value={view as NoteView} onChange={(next) => onChangeView(index, next)} />
        ) : view.kind === 'diagram' ? (
          <VisualizationDiagramCanvas value={view as DiagramView} onChange={(next) => onChangeView(index, next)} />
        ) : (
          <div className="p-2 text-xs text-[#9ca3af]">"{view.kind}" doesn't have a visual editor yet.</div>
        )}
      </div>
      </div>
      </div>
    </div>
  );
}

const nodeTypes = { viewBlock: ViewBlockNode };

const GAP = 24;

/**
 * Fixed per-kind height used to stack blocks — `array`/`stack`/`grid`
 * blocks are a compact row/grid of cells; `tree`/`graph` blocks embed a
 * second, ~320px-tall React Flow canvas of their own and run much taller.
 * These are generous overestimates (better to leave a gap than overlap),
 * and — deliberately — never reconciled against the real measured DOM size
 * at runtime: an earlier version re-flowed positions live as each block's
 * actual size came in from `onNodesChange`'s `dimensions` change, which
 * created a feedback loop (relayout → dimension change → relayout →
 * …) that kept nudging a block's position indefinitely, so a click could
 * never land on a still target. A one-time, slightly-too-generous static
 * guess a trainer can drag around is far more usable than a "smarter"
 * layout that never settles.
 */
const HEIGHT_GUESS: Record<View['kind'], number> = {
  array: 260,
  stack: 260,
  grid: 320,
  linkedlist: 200,
  tree: 700,
  graph: 700,
  note: 220,
  diagram: 500,
};

function Canvas({
  views,
  onChangeView,
  onRemoveView,
  onDuplicateView,
  pendingNewViewPosition,
  onConsumedPendingPosition,
  onDropViewKind,
}: Props) {
  const { screenToFlowPosition } = useReactFlow();
  /**
   * Position is session-local only — NEVER written back into the persisted
   * `View` (see `Visualizer/types.ts`: renderers own all geometry, which is
   * what lets the same script render on a 1440px screen and a 390px phone).
   * Kept in a ref (not state) so free-form dragging feels like a real canvas
   * across step switches within one editing session, without ever risking
   * that arrangement leaking into what gets saved.
   *
   * Blocks stack in a single column, auto-flowing top to bottom by
   * `HEIGHT_GUESS`, rather than a fixed grid — block heights vary wildly (a
   * compact array row vs. a tree/graph block embedding its own 320px
   * canvas), and a fixed-size grid cell either wastes huge amounts of space
   * or, worse, lets a tall block overlap the one "beside" it, burying its
   * buttons/inputs under the other's higher z-index so clicks silently land
   * on the wrong block. Once placed, a block can still be dragged anywhere —
   * this only decides where a NEWLY added block starts.
   */
  const positionsRef = useRef<Record<number, { x: number; y: number }>>({});

  /**
   * `onChangeView`/`onRemoveView`/`onDuplicateView` arrive as fresh inline
   * closures on EVERY parent render (`VisualizationStageEditor` builds them
   * inline in its JSX). Feeding those identities straight into node `data`
   * made `buildNodes` — and the `data`-resync effect below, which lists them
   * as deps — rebuild every node object on every render. A new `nodes` array
   * identity is exactly what React Flow's prop→store sync watches for (it
   * compares `props.nodes` by REFERENCE and calls its store's `setNodes`),
   * so each rebuild pushed a store update, which re-rendered this component,
   * which produced new closures, which rebuilt the nodes again — an
   * unbounded loop that React eventually killed with "Maximum update depth
   * exceeded", taking the whole editor route down with it. Holding them in a
   * ref and handing nodes STABLE wrappers keeps node `data` identity-stable
   * while still always invoking the latest callback.
   */
  const callbacksRef = useRef({ onChangeView, onRemoveView, onDuplicateView });
  callbacksRef.current = { onChangeView, onRemoveView, onDuplicateView };

  const stableOnChangeView = useCallback(
    (index: number, next: View) => callbacksRef.current.onChangeView(index, next),
    []
  );
  const stableOnRemoveView = useCallback((index: number) => callbacksRef.current.onRemoveView(index), []);
  const stableOnDuplicateView = useCallback((index: number) => callbacksRef.current.onDuplicateView(index), []);

  const stackedY = (index: number) => {
    let y = 24;
    for (let i = 0; i < index; i++) y += (HEIGHT_GUESS[views[i]?.kind] ?? 300) + GAP;
    return y;
  };

  const buildNodes = useCallback((): Node[] => {
    return views.map((view, index) => {
      const position = positionsRef.current[index] ?? { x: 24, y: stackedY(index) };
      positionsRef.current[index] = position;
      return {
        id: `view-${index}`,
        type: 'viewBlock',
        position,
        // A hint only — React Flow re-measures the real DOM size once
        // mounted. Without SOME hint, a node stays `visibility: hidden`
        // until its `ResizeObserver` callback fires, which jsdom's stubbed
        // `ResizeObserver` (see `src/test/setup.ts`) never does, making
        // every block permanently invisible under test.
        initialWidth: 700,
        initialHeight: HEIGHT_GUESS[view.kind] ?? 300,
        data: {
          view,
          index,
          onChangeView: stableOnChangeView,
          onRemoveView: stableOnRemoveView,
          onDuplicateView: stableOnDuplicateView,
        } satisfies BlockData,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [views, stableOnChangeView, stableOnRemoveView, stableOnDuplicateView]);

  const [nodes, setNodes] = useNodesState(buildNodes());

  // Resync POSITIONS only when the set of views actually changed shape
  // (added/removed/reordered kind) — repositioning on every keystroke would
  // fight a trainer's own in-progress drag.
  const fingerprint = views.map((v, i) => `${i}:${v.kind}`).join('|');
  useEffect(() => {
    setNodes(buildNodes());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint]);

  /**
   * Resync each node's `data.view` on EVERY `views` change, not just shape
   * changes — a node's `data` is a snapshot taken when `buildNodes` last ran
   * (mount, or the last `fingerprint` change above), so without this a
   * value/mark edit inside a block (e.g. clicking "+ Value") updates the
   * real `steps` state correctly but the on-screen block keeps re-rendering
   * that stale, frozen `data.view` forever — invisibly to the trainer, since
   * nothing about the node's identity or position ever signals staleness.
   * Tree/Graph blocks mask this (they manage their own node/edge state
   * internally, decoupled from `data.view` after mount), which is why this
   * surfaced only for Array/Stack/Grid, whose canvases are fully controlled
   * by the `value` prop. Deliberately separate from the position-resync
   * effect above: touching only `data`, never `position`, so it can safely
   * run on every render without fighting an in-progress drag or reintroducing
   * the measure→relayout feedback loop a similar-looking effect once caused.
   */
  useEffect(() => {
    setNodes((current) => {
      // Returning the SAME array when no `view` actually changed is what keeps
      // this effect from re-entering React Flow's reference-compared prop→store
      // sync on every render (see `callbacksRef` above). Rebuilding
      // unconditionally — as this once did — is an update with no fixed point.
      let changed = false;
      const next = current.map((n) => {
        const data = n.data as unknown as BlockData;
        const view = views[data.index];
        if (view === data.view) return n;
        changed = true;
        return { ...n, data: { ...data, view } satisfies BlockData };
      });
      return changed ? next : current;
    });
  }, [views, setNodes]);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      setNodes((current) => {
        const next = applyNodeChanges(changes, current);
        next.forEach((n) => {
          const idx = (n.data as unknown as BlockData).index;
          positionsRef.current[idx] = n.position;
        });
        return next;
      });
    },
    [setNodes]
  );

  // The newest view just arrived via a palette drop — place it at the drop point
  // instead of the default stacked position, then clear the pending flag so a later
  // structural change (e.g. removing a block) doesn't reapply a stale drop point.
  useEffect(() => {
    if (!pendingNewViewPosition) return;
    positionsRef.current[pendingNewViewPosition.index] = pendingNewViewPosition.position;
    setNodes(buildNodes());
    onConsumedPendingPosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingNewViewPosition]);

  const onDragOver = useCallback((e: React.DragEvent) => {
    if (e.dataTransfer.types?.includes('application/x-kodprep-view-kind')) e.preventDefault();
  }, []);

  return (
    <div style={{ height: 560 }} className="relative rounded-lg border-2 border-dashed border-[#e5e7eb]">
      {views.length === 0 && (
        <p className="pointer-events-none absolute inset-x-0 top-8 z-10 text-center text-xs text-[#9ca3af]">
          Click an element on the left to add it here — drag blocks to arrange them.
        </p>
      )}
      <ReactFlow
        nodes={nodes}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onDragOver={onDragOver}
        onDrop={(e) => {
          e.preventDefault();
          const kind = e.dataTransfer.getData('application/x-kodprep-view-kind') as ViewKind | '';
          if (kind) onDropViewKind(kind, screenToFlowPosition({ x: e.clientX, y: e.clientY }));
        }}
        minZoom={0.2}
      >
        <Background />
        {/* Blocks stack starting at x=24 from the top, so the pane's default
            bottom-left corner is exactly where a block's own controls (its
            header, "+ Value"/"+ Add node" buttons) end up — this control
            panel would render on top of them, silently eating clicks aimed
            at the block underneath. Top-right is never occupied by a block. */}
        <Controls showInteractive={false} position="top-right" />
      </ReactFlow>
    </div>
  );
}

/** One persistent, freely-arrangeable canvas for all of a step's views — see `VisualizationStageEditor`, which owns narration/state/steps and delegates only this area. */
export function VisualizationCanvasStage(props: Props) {
  return (
    <ReactFlowProvider>
      <Canvas {...props} />
    </ReactFlowProvider>
  );
}
